import React from "react";
import { Badge, Button, Card, Col, Container, Row } from "react-bootstrap";
import { ArrowRight } from "react-bootstrap-icons";
import { GoArrowUpRight } from "react-icons/go";
import { useNavigate } from "react-router-dom";
import {
  primaryColor,
  secondaryColor,
  textDarkColor,
} from "../../../Constants/Colors";
import GetInTouch from "../../../Components/GetInTouch";
import { useGetWorksQuery } from "../../../redux/api/WorksApi/worksApi";
import { useGetSettingsQuery } from "../../../redux/api/SettingsApi/settingsApi";
import LoaderSpinner from "../../../Components/LoaderSpinner";
import SEO from "../../../Components/SEO";

const Works = () => {
  const navigate = useNavigate();
  const { data: settings } = useGetSettingsQuery();
  const { data, isLoading, error } = useGetWorksQuery({
    status: true,
    sortBy: "display_order",
    order: "asc",
    limit: 100,
  });

  const works = data?.works || [];

  if (isLoading) {
    return <LoaderSpinner />;
  }

  if (error) {
    return (
      <Container className="text-center my-5">
        <h4 className="text-danger">
          Failed to load works. Please try again later.
        </h4>
      </Container>
    );
  }

  return (
    <Container>
      <SEO
        title="Our Works | Transforming Ideas into Reality | Ideal"
        description={settings?.meta_description}
        keywords={settings?.meta_keyword}
      />
      <div className="text-center my-2">
        <Button
          variant="primary"
          style={{
            backgroundColor: "#007bff",
            border: "none",
            borderRadius: "20px",
            padding: "5px 20px",
            fontSize: "14px",
          }}
        >
          Our Works
        </Button>
        <Row>
          <Col md={10} xl={10} className="mx-auto">
            <h4
              className="fw-bold mb-0 py-3 text-start"
              style={{
                background: "linear-gradient(to right, #0070c9, #004e92)",
                WebkitBackgroundClip: "text",
                WebkitTextFillColor: "transparent",
                lineHeight: "1.3",
              }}
            >
              Explore how we transform complex business challenges into
              scalable, high-performance digital solutions:
            </h4>
          </Col>
        </Row>
      </div>

      <Row className="mb-5">
        <Col xl={12}>
          <Row xs={1} md={1} xl={1} className="g-4">
            {works.map((project, idx) => (
              <Col
                md={6}
                xl={6}
                key={project.id || idx}
                onClick={() => navigate(`/works/${project.slug}`)}
                className="cursor-pointer"
              >
                <Card
                  className="h-100 custom-work-card rounded-4 shadow-sm overflow-hidden"
                  style={{
                    background:
                      project.card_gradient ||
                      "linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%)",
                    border: "2px solid #eee",
                  }}
                >
                  <div className="d-md-none">
                    <Card.Img
                      src={project.visual_laptop || project.previewImage}
                      alt={project.title}
                      className="img-fluid rounded-top-4"
                      style={{
                        width: "100%",
                        height: "auto",
                        objectFit: "cover",
                      }}
                    />
                  </div>

                  <Row>
                    <Col
                      xs={12}
                      className="p-4 pb-0 d-flex justify-content-between align-items-center"
                    >
                      <Badge
                        bg="light"
                        text="dark"
                        className="px-3 py-2 rounded-pill fw-semibold"
                      >
                        Development Project
                      </Badge>
                      <div
                        className="rounded-circle d-flex align-items-center justify-content-center bg-white shadow-sm"
                        style={{ width: "36px", height: "36px" }}
                      >
                        <GoArrowUpRight size={23} />
                      </div>
                    </Col>
                  </Row>

                  <Row className="g-0 align-items-center">
                    <Col
                      md={7}
                      className="p-4 d-flex flex-column justify-content-between"
                    >
                      <div>
                        <Card.Title className="fw-bold fs-5 mb-2">
                          {project.title}
                        </Card.Title>
                        <Card.Text
                          className="text-muted"
                          style={{ textAlign: "justify", fontSize: "14px" }}
                        >
                          {project.overview_summary || project.tagline}
                        </Card.Text>
                      </div>
                      <div className="mt-3">
                        <Button
                          variant="link"
                          className="p-0 text-dark fw-semibold d-flex align-items-center gap-1"
                        >
                          Know more <ArrowRight />
                        </Button>
                      </div>
                    </Col>
                    <Col md={5} className="d-none d-md-block">
                      <Card.Img
                        src={project.visual_laptop || project.previewImage}
                        alt={project.title}
                        className="img-fluid rounded-end-4"
                        style={{
                          minHeight: "180px",
                        }}
                      />
                    </Col>
                  </Row>
                </Card>
              </Col>
            ))}
          </Row>
        </Col>
      </Row>
      <GetInTouch
        radius={60}
        description="Leverage our expertise to transform your digital presence and elevate your brand."
        bgColor="light"
        borderColor={secondaryColor}
        textColor={textDarkColor}
        subTextColor={primaryColor}
        circleTextColor={textDarkColor}
        descriptionColor={textDarkColor}
      />
    </Container>
  );
};

export default Works;
